import React, { useEffect, useRef, useState } from 'react';

import dayjs from 'dayjs';

import { MDText } from 'i18n-react';

import { Button, IconText, WarningIcon } from '@motorway/mw-highway-code';
import { INFOBOX_VARIANT } from '@motorway/mw-highway-code/enums';
import { toast } from '@motorway/mw-highway-code/pre-alpha';

import { SP_SELLER_COLLECTION_PREFERENCE } from 'Utilities/analytics/analyticsSellerCollectionPreference';

import { LoadingArrow } from 'Storybook/components/local/LoadingArrow/LoadingArrow';

import { isDatesNotAvailableError, isSellerPreferenceAlreadySubmitted, isSellerPreferenceNotFoundError } from '../../../vehicleCollection/utils/collectionPreferences';
import LocalTexts from '../../DocumentCapture.texts.json';
import DocumentCaptureButtons from '../../shared/DocumentCaptureButtons/DocumentCaptureButtons';
import useGetAvailability from '../hooks/useGetAvailability';
import { useUpdatePreferences } from '../hooks/useUpdatePreferences';

import DateChipSelect from './DateChipSelect/DateChipSelect';
import DateSelectionIndicator from './DateSelectionIndicator/DateSelectionIndicator';

import styles from './CollectionPreferences.scss';

export type DateOption = {
  [key: string]: boolean;
};

export type CollectionPreferencesProps = {
  enquiryId?: number;
  expiryAt: string | undefined;
  handleSetCompleteStatus: () => void;
  minimumSelectedDates?: number;
}

const LocalT = new MDText(LocalTexts.dealerDetails);

const CollectionPreferences = ({
  enquiryId,
  expiryAt,
  handleSetCompleteStatus,
  minimumSelectedDates = 1,
}: CollectionPreferencesProps) => {
  const minLimit = minimumSelectedDates;
  const maxlimit = 5;

  const [dateSelections, setDateSelections] = useState<DateOption>({});
  const [noLongerAvailableDates, setNoLongerAvailableDates] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const remountChipsKey = useRef<number>(0);
  const { availableDates, loading, refresh: refreshDates } = useGetAvailability() || {};
  const previousAvailableDates = useRef<string[]>([]);

  const handlePreferencesFailure = (err: unknown) => {
    window?.Sentry?.captureException?.(err);
    if (isDatesNotAvailableError(err as Error)) {
      // This check prevents updating the array multiple times with the newest availability
      // so that multiple submits with different availablity wont break the UI
      if (!previousAvailableDates.current.length) {
        previousAvailableDates.current = availableDates ?? [];
      }
      refreshDates();
    }

    if (isSellerPreferenceNotFoundError(err as Error)) {
      // user can refresh the page here resetting scp state
      toast({
        content: LocalT.translate('sellerPreferenceNotFoundError.content'),
        heading: LocalT.translate('sellerPreferenceNotFoundError.title'),
        id: 'seller-collection-preferences-toast',
        variant: INFOBOX_VARIANT.ERROR,
      });
    }

    if (isSellerPreferenceAlreadySubmitted(err as Error)) {
      toast({
        content: LocalT.translate('sellerPreferenceAlreadySubmittedError.content'),
        heading: LocalT.translate('sellerPreferenceAlreadySubmittedError.title'),
        id: 'seller-collection-preferences-toast',
        variant: INFOBOX_VARIANT.INFO,
      });
      handleSetCompleteStatus();
    }

    setIsSubmitting(false);
  };

  const { updatePreferences } = useUpdatePreferences({
    onError: handlePreferencesFailure,
    onSuccess: handleSetCompleteStatus,
  });

  // When page loads
  useEffect(() => {
    SP_SELLER_COLLECTION_PREFERENCE.COLLECTION_PREFERENCES_PAGE();
  }, []);

  useEffect(() => {
    if (availableDates && !loading) {
      if (previousAvailableDates.current.length > 0) {
        const difference = previousAvailableDates.current?.filter(
          (previouslyAvailableDate) => !availableDates.includes(previouslyAvailableDate),
        );
        setNoLongerAvailableDates(difference);

        // we have to flatten dates to make them a string as snowplow does not accept string array
        const flattenDates = difference.join(',');
        SP_SELLER_COLLECTION_PREFERENCE.COLLECTION_PREFERENCES_DATES_UNAVAILABLE(flattenDates);
        remountChipsKey.current += 1;

        setDateSelections(
          (prevState) => previousAvailableDates.current.reduce((acc: Record<string, boolean>, date: string) => (
            {
              ...acc,
              [date]: difference.includes(date) ? false : prevState[date],
            }), {}),
        );
      } else {
        setDateSelections(availableDates.reduce((acc, date) => ({ ...acc, [date]: false }), {}));
      }

      setIsSubmitting(false);
    }
  }, [availableDates, loading]);

  const onSubmit = async () => {
    setIsSubmitting(true);
    const selections = Object.keys(dateSelections).filter((date) => dateSelections[date]);
    SP_SELLER_COLLECTION_PREFERENCE.COLLECTION_PREFERENCES_DATES_SUBMITTED();

    await updatePreferences({ datePreferences: selections, dpEnquiryId: enquiryId });
  };

  const selectedDatesCount = Object.values(dateSelections).filter(Boolean).length;
  const selectedDatesMinLimit = selectedDatesCount < minLimit;
  const selectedDatesMaxLimit = selectedDatesCount >= maxlimit;

  if (loading && !previousAvailableDates.current.length) {
    return <div className={styles.loading}><LoadingArrow /></div>;
  }

  return (
    <div className={styles.collectionPreferences}>
      <h3>{LocalT.translate('collectionPreferences')}</h3>
      <p className={styles.subtitle}>
        {LocalT.translate('collectionAvailabilitySubtitle')}
      </p>
      <div className={styles.preferences}>
        <DateChipSelect
          key={remountChipsKey.current}
          blockedDates={noLongerAvailableDates}
          dateOptions={dateSelections}
          defaultValue={Object.keys(dateSelections).filter((date) => dateSelections[date])}
          limitReached={selectedDatesMaxLimit}
          setDateSelections={setDateSelections}
        />
        {noLongerAvailableDates.length > 0 && (
          <div className={styles.error}>
            <IconText
              alignIcon="left"
              icon={WarningIcon}
              text={LocalT.translate(
                noLongerAvailableDates.length === 1
                  ? 'collectionPreferencesSingleDatesNoLongerAvailableError'
                  : 'collectionPreferencesMultiDatesNoLongerAvailableError',
                { dates: noLongerAvailableDates.map((date) => dayjs(date).format('ddd D MMMM')) },
              )}
              variant="small--regular"
            />
          </div>
        )}
      </div>
      <DateSelectionIndicator expiryTime={expiryAt} selectedCount={selectedDatesCount} />
      <DocumentCaptureButtons>
        <Button disabled={selectedDatesMinLimit} icon="chevron" label={LocalT.translate('submitPreferences')} loading={isSubmitting} onClick={onSubmit} />
      </DocumentCaptureButtons>
    </div>
  );
};

// React componetn for
export default CollectionPreferences;
